// Stranka Zastupy — kdo za koho schvaluje v dobe nepritomnosti (dovolena apod.).
//
// - SCHVALOVATEL vidi a spravuje sve zastupy (a vidi, koho sam zastupuje),
// - ADMIN vidi a spravuje zastupy vsech schvalovatelu,
// - TECHNIK_INT (muze byt zastupcem) jen vidi, koho a kdy zastupuje.
// Pravidla (kdo smi menit ktery zaznam, role, prekryvy obdobi) vynucuje backend
// (ZastupyController + ZastupService); tady je jen UX zrcadlo.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { Zastup, ZastupInput, ZastupMoznosti } from '@/types/api';
import { formatDateCs } from '@/lib/utils';
import { isoDate } from '@/lib/dateWeek';
import { usePermissions } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

const QUERY_KEY = ['zastupy'] as const;
const ENDPOINT = '/zastupy';

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; item: Zastup }
  | { mode: 'delete'; item: Zastup };

/** Stav zastupu vuci dnesku — jen pro zobrazeni badge. */
function stavZastupu(z: Zastup, dnesIso: string): 'probiha' | 'naplanovan' | 'ukoncen' {
  if (dnesIso < z.od) return 'naplanovan';
  if (dnesIso > z.doDate) return 'ukoncen';
  return 'probiha';
}

export function ZastupyPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, isAdmin, canSpravovatZastupy, canVidetZastupy } = usePermissions();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const dnesIso = isoDate(new Date());

  const listQuery = useQuery<Zastup[]>({
    queryKey: QUERY_KEY,
    queryFn: () => api<Zastup[]>(ENDPOINT),
    enabled: canVidetZastupy,
  });

  const moznostiQuery = useQuery<ZastupMoznosti>({
    queryKey: ['zastupy', 'moznosti'],
    queryFn: () => api<ZastupMoznosti>(`${ENDPOINT}/moznosti`),
    enabled: canSpravovatZastupy,
  });

  const createMutation = useMutation({
    mutationFn: (input: ZastupInput) => api<Zastup>(ENDPOINT, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zástup nastaven', description: 'Zástupce dostane e-mailové upozornění.' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Nastavení zástupu selhalo', description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ZastupInput }) =>
      api<Zastup>(`${ENDPOINT}/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zástup upraven' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Úprava zástupu selhala', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api<void>(`${ENDPOINT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zástup zrušen' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Zrušení zástupu selhalo', description: err.message, variant: 'destructive' }),
  });

  /** Smi prihlaseny uzivatel menit tento zaznam? Mirror ZastupyController.OverVlastnictvi. */
  const muzeMenit = (z: Zastup) => isAdmin || z.schvalovatelId === user?.id;

  if (!canVidetZastupy) {
    return (
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Zástupy</h2>
        <p className="text-sm text-muted-foreground">
          Zástupy schvalovatelů vidí jen uživatelé naší firmy (technik, schvalovatel,
          administrátor).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Zástupy</h2>
          <p className="text-sm text-muted-foreground">
            Kdo za koho schvaluje operativy v době nepřítomnosti (dovolená, služební cesta).
            Po nastavenou dobu přechází na zástupce schvalování i e-mailové notifikace.
            Zástupcem může být kdokoliv z naší firmy — technik, schvalovatel nebo administrátor.
          </p>
        </div>
        {canSpravovatZastupy && (
          <Button onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="mr-2 h-4 w-4" />
            Nový zástup
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isAdmin
              ? 'Zástupy všech schvalovatelů'
              : canSpravovatZastupy
                ? 'Moje zástupy'
                : 'Zástupy, ve kterých figuruji'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zastoupený schvalovatel</TableHead>
                    <TableHead>Zástupce</TableHead>
                    <TableHead className="w-28">Od</TableHead>
                    <TableHead className="w-28">Do</TableHead>
                    <TableHead className="w-32">Stav</TableHead>
                    <TableHead className="w-28 text-right">Akce</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(listQuery.data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        Zatím žádné zástupy. Před dovolenou si nastavte, kdo bude schvalovat za vás.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (listQuery.data ?? []).map((z) => {
                      const stav = stavZastupu(z, dnesIso);
                      const editable = muzeMenit(z);
                      const tooltip = editable
                        ? undefined
                        : 'Cizí zástup může měnit jen zastoupený schvalovatel nebo administrátor.';
                      return (
                        <TableRow key={z.id}>
                          <TableCell className="font-medium">
                            {z.schvalovatelJmeno}
                            {z.schvalovatelId === user?.id && (
                              <span className="ml-1 text-xs text-muted-foreground">(vy)</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {z.zastupceJmeno}
                            {z.zastupceId === user?.id && (
                              <span className="ml-1 text-xs text-muted-foreground">(vy)</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDateCs(z.od)}</TableCell>
                          <TableCell>{formatDateCs(z.doDate)}</TableCell>
                          <TableCell>
                            {stav === 'probiha' ? (
                              <Badge variant="success">Probíhá</Badge>
                            ) : stav === 'naplanovan' ? (
                              <Badge variant="warning">Naplánován</Badge>
                            ) : (
                              <Badge variant="secondary">Ukončen</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!editable}
                              title={tooltip ?? 'Upravit'}
                              onClick={() => setDialog({ mode: 'edit', item: z })}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!editable}
                              title={tooltip ?? 'Zrušit zástup'}
                              onClick={() => setDialog({ mode: 'delete', item: z })}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ZastupFormDialog
        state={dialog}
        moznosti={moznostiQuery.data}
        isAdmin={isAdmin}
        currentUserId={user?.id ?? ''}
        currentUserJmeno={user?.jmeno ?? ''}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={(input, id) => {
          if (id) updateMutation.mutate({ id, input });
          else createMutation.mutate(input);
        }}
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDeleteDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onConfirm={(id) => deleteMutation.mutate(id)}
        submitting={deleteMutation.isPending}
      />
    </div>
  );
}

interface FormDialogProps {
  state: DialogState;
  moznosti: ZastupMoznosti | undefined;
  isAdmin: boolean;
  currentUserId: string;
  currentUserJmeno: string;
  onClose: () => void;
  onSubmit: (input: ZastupInput, id?: string) => void;
  submitting: boolean;
}

function ZastupFormDialog({
  state,
  moznosti,
  isAdmin,
  currentUserId,
  currentUserJmeno,
  onClose,
  onSubmit,
  submitting,
}: FormDialogProps) {
  const open = state.mode === 'create' || state.mode === 'edit';
  const editItem = state.mode === 'edit' ? state.item : null;

  const [schvalovatelId, setSchvalovatelId] = useState('');
  const [zastupceId, setZastupceId] = useState('');
  const [od, setOd] = useState('');
  const [doDate, setDoDate] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setSchvalovatelId(editItem.schvalovatelId);
      setZastupceId(editItem.zastupceId);
      setOd(editItem.od);
      setDoDate(editItem.doDate);
    } else {
      // Ne-admin muze nastavit zastup jen sam za sebe.
      setSchvalovatelId(isAdmin ? '' : currentUserId);
      setZastupceId('');
      setOd('');
      setDoDate('');
    }
  }, [open, editItem, isAdmin, currentUserId]);

  // Zastupce nesmi byt zastoupeny sam sebou.
  const zastupci = useMemo(
    () => (moznosti?.zastupci ?? []).filter((u) => u.id !== schvalovatelId),
    [moznosti, schvalovatelId],
  );

  const datumOk = od !== '' && doDate !== '' && od <= doDate;
  const valid = schvalovatelId !== '' && zastupceId !== '' && zastupceId !== schvalovatelId && datumOk;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({ schvalovatelId, zastupceId, od, doDate }, editItem?.id);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editItem ? 'Upravit zástup' : 'Nový zástup'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="zastup-za">Za koho (zastoupený schvalovatel)</Label>
            {isAdmin && !editItem ? (
              <Select value={schvalovatelId} onValueChange={setSchvalovatelId}>
                <SelectTrigger id="zastup-za">
                  <SelectValue placeholder="Vyberte schvalovatele…" />
                </SelectTrigger>
                <SelectContent>
                  {(moznosti?.schvalovatele ?? []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.jmeno}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              // Vlastni zastup (ne-admin) / editace: zastoupeneho nelze menit.
              <Input
                id="zastup-za"
                value={editItem ? editItem.schvalovatelJmeno : currentUserJmeno}
                disabled
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zastup-kdo">Zástupce (bude schvalovat)</Label>
            <Select value={zastupceId} onValueChange={setZastupceId}>
              <SelectTrigger id="zastup-kdo">
                <SelectValue placeholder="Vyberte zástupce…" />
              </SelectTrigger>
              <SelectContent>
                {zastupci.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.jmeno}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Zastupovat může kdokoliv z naší firmy — technik, schvalovatel nebo administrátor.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="zastup-od">Od</Label>
              <Input id="zastup-od" type="date" required value={od} onChange={(e) => setOd(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="zastup-do">Do (včetně)</Label>
              <Input
                id="zastup-do"
                type="date"
                required
                value={doDate}
                min={od || undefined}
                onChange={(e) => setDoDate(e.target.value)}
              />
            </div>
          </div>
          {od !== '' && doDate !== '' && !datumOk && (
            <p className="text-sm text-destructive">Datum „od" musí být nejpozději v den „do".</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting || !valid}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmProps {
  state: DialogState;
  onClose: () => void;
  onConfirm: (id: string) => void;
  submitting: boolean;
}

function ConfirmDeleteDialog({ state, onClose, onConfirm, submitting }: ConfirmProps) {
  const open = state.mode === 'delete';
  const item = state.mode === 'delete' ? state.item : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zrušit zástup?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Opravdu chcete zrušit zástup, kdy <strong>{item?.zastupceJmeno}</strong> schvaluje za{' '}
          <strong>{item?.schvalovatelJmeno}</strong> ({item && formatDateCs(item.od)} –{' '}
          {item && formatDateCs(item.doDate)})? Schvalování se okamžitě vrátí zastoupenému.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zpět
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={() => item && onConfirm(item.id)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Zrušit zástup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
